/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment } from 'react'
import { FormattedMessage, WrappedComponentProps, defineMessages, injectIntl } from 'react-intl'

import {
  EuiAccordion,
  EuiButton,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPanel,
  EuiSpacer,
  EuiSteps,
  EuiText,
} from '@elastic/eui'

import { securityUrl } from '@/lib/urlBuilder'

import type { AllProps } from './types'
import type { FunctionComponent } from 'react'

const messages = defineMessages({
  step1Title: {
    id: `externalTrustRelationshipInstructions.step1.title`,
    defaultMessage: `Download the CA certificate`,
  },
  step2Title: {
    id: `externalTrustRelationshipInstructions.step2.title`,
    defaultMessage: `Establish trust with the other ECE environment`,
  },
  step3Title: {
    id: `externalTrustRelationshipInstructions.step3.title`,
    defaultMessage: `Configure the deployments`,
  },
})

const ExternalRelationshipInstructions: FunctionComponent<AllProps & WrappedComponentProps> = ({
  deployment,
  intl: { formatMessage },
  certificateAuthority,
}) => (
  <Fragment>
    <EuiCallOut color='success'>
      <EuiText>
        <FormattedMessage
          id='deploymentTrustManagement.external-cert.success'
          defaultMessage='Trusted environment successfully added'
        />
      </EuiText>
    </EuiCallOut>
    <EuiSpacer size='l' />
    <EuiText>
      <p>
        <FormattedMessage
          id='deploymentTrustManagement.external-cert.intro'
          defaultMessage='Have you already set up trust from the other environment?'
        />
      </p>
    </EuiText>
    <EuiSpacer size='l' />
    <EuiPanel>
      <EuiAccordion
        id={`instructions-yes`}
        buttonContent={
          <b>
            <FormattedMessage
              id='deploymentTrustManagement.external-cert.yes'
              defaultMessage='Yes, I have already set up trust from the other environment.'
            />
          </b>
        }
      >
        <EuiText>
          <EuiSpacer size='s' />
          <p>
            <FormattedMessage
              id='deploymentTrustManagement.external-cert.yes-body'
              defaultMessage="You're all set. You can now add remote clusters in Kibana and perform cross-cluster operations between both environments."
            />
          </p>
        </EuiText>
      </EuiAccordion>
    </EuiPanel>
    <EuiSpacer size='l' />
    <EuiPanel>
      <EuiAccordion
        id={`instructions-no`}
        buttonContent={
          <b>
            <FormattedMessage
              id='deploymentTrustManagement.external-cert.no'
              defaultMessage='No, I have NOT set up trust from the other environment yet.'
            />
          </b>
        }
      >
        <EuiText>
          <p>
            <EuiSpacer size='s' />
            <FormattedMessage
              id='deploymentTrustManagement.external-cert.no-body'
              defaultMessage="To enable cross-cluster operations, both environments must trust each other. That means that if you haven't already done so, you also need to perform this configuration in the newly trusted environment. To do this:"
            />
          </p>
        </EuiText>
        <EuiSpacer />
        <EuiSteps
          steps={[
            {
              title: formatMessage(messages.step1Title),
              children: renderStep1(certificateAuthority),
            },
            {
              title: formatMessage(messages.step2Title),
              children: renderStep2(),
            },
            {
              title: formatMessage(messages.step3Title),
              children: renderStep3(),
            },
          ]}
        />
      </EuiAccordion>
    </EuiPanel>
    <EuiSpacer />
    <EuiFlexGroup direction='column' alignItems='flexEnd'>
      <EuiFlexItem>
        <EuiButton fill={true} href={securityUrl(deployment.id)}>
          <FormattedMessage id='externalTrustRelationshipInstructions.done' defaultMessage='Done' />
        </EuiButton>
      </EuiFlexItem>
    </EuiFlexGroup>
  </Fragment>
)

function renderStep1(certificateAuthority): JSX.Element {
  return (
    <Fragment>
      <EuiText>
        <FormattedMessage
          id='externalTrustRelationshipInstructions.step1.body'
          defaultMessage='Download the CA certificate of this deployment. You need it to configure trust with this deployment from your ECE environment.'
        />
      </EuiText>
      <EuiSpacer size='m' />
      {renderCertButton(certificateAuthority)}
    </Fragment>
  )
}

function renderStep2(): JSX.Element {
  return (
    <EuiText>
      <FormattedMessage
        id='externalTrustRelationshipInstructions.step2.body'
        defaultMessage='Go to your other ECE environment. Open the Trust management page under Platform, and use the CA certificate you just downloaded to configure your ECE environment to trust this ECE environment.
          '
      />
    </EuiText>
  )
}

function renderStep3(): JSX.Element {
  return (
    <EuiText>
      <FormattedMessage
        id='externalTrustRelationshipInstructions.step3.body'
        defaultMessage='Then, for each deployment of the other ECE environment that you want to use for cross-cluster operations, go to its Security page and add the trust configuration performed at the previous step.
          '
      />
    </EuiText>
  )
}

function renderCertButton(certificateAuthority) {
  const [cert] = certificateAuthority?.public_certificates || []

  const text = (
    <FormattedMessage
      id='externalTrustRelationshipInstructions.downloadCertButton'
      defaultMessage='Download CA certificate'
    />
  )

  if (!cert) {
    return <EuiButton isLoading={true}>{text}</EuiButton>
  }

  const blob = new window.Blob([cert.pem || ``], {
    type: `application/x-x509-ca-cert;charset=utf-8`,
  })
  const fileName = `${cert.metadata.fingerprint}.crt`
  const contents = window.URL.createObjectURL(blob)

  return (
    <EuiButton download={fileName} href={contents} iconType='download'>
      {text}
    </EuiButton>
  )
}

export default injectIntl(ExternalRelationshipInstructions)
